import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { clientPath, useSwitchToOwningClient } from '../lib/clientLink.js';
import WhyThisPost from '../components/WhyThisPost.jsx';
import StatusChip from '../components/StatusChip.jsx';
import PostingInstructions from '../components/PostingInstructions.jsx';

const SIGNED_URL_TTL = 600;

// Package pages reference images as images/<file>; inside srcDoc there is no
// base URL, so swap each one for a signed URL from the private bucket.
async function resolvePackageImages(html, packagePath) {
  const dir = packagePath.replace(/package\.zip$/, '');
  const names = Array.from(new Set(Array.from(html.matchAll(/src="images\/([^"]+)"/g)).map((m) => m[1])));
  if (!names.length) return html;
  const { data, error } = await supabase.storage.from('post-packages')
    .createSignedUrls(names.map((n) => `${dir}images/${n}`), SIGNED_URL_TTL);
  if (error || !data) return html;
  let out = html;
  data.forEach((d, i) => {
    if (d?.signedUrl) out = out.split(`src="images/${names[i]}"`).join(`src="${d.signedUrl}"`);
  });
  return out;
}

export default function BlogReader() {
  const { slug } = useParams();
  // activeClient carries the platform, which picks the posting instructions.
  const { activeId, activeClient, isAgency } = useAuth();
  const [state, setState] = useState({ loading: true, error: null, row: null, html: null });
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState({ loading: true, error: null, row: null, html: null });
      if (!activeId || !slug) return;

      const { data: rows, error: qErr } = await supabase
        .from('v_portal_posts')
        .select('*')
        .eq('client_id', activeId)
        .eq('slug', slug)
        .limit(1);
      if (cancelled) return;
      if (qErr) { setState({ loading: false, error: qErr.message, row: null, html: null }); return; }
      const row = rows && rows[0];
      if (!row) { setState({ loading: false, error: 'Post not found.', row: null, html: null, notFound: true }); return; }

      // Session 6 follow-up (fix B): an approved, not-yet-published post shows
      // the full preview (site shell where configured) with the uploaded photos
      // inlined, exactly as it will look. Published / delivered posts show the
      // package page with its images resolved to signed URLs. No text-only fallback.
      // Session 8: a running publish ('publishing') shows the same preview.
      const usePreview = (row.status === 'ready' || row.status === 'publishing') && row.preview_path;
      const htmlPath = usePreview ? row.preview_path : row.package_path.replace(/package\.zip$/, 'index.html');
      const { data: signed, error: sErr } = await supabase.storage
        .from('post-packages').createSignedUrl(htmlPath, SIGNED_URL_TTL);
      if (cancelled) return;
      if (sErr) { setState({ loading: false, error: sErr.message, row, html: null }); return; }
      let html = null;
      try {
        const resp = await fetch(signed.signedUrl);
        if (resp.ok) html = await resp.text();
      } catch (e) {
        console.warn('reader fetch failed:', e);
      }
      if (html && !usePreview) html = await resolvePackageImages(html, row.package_path);
      if (cancelled) return;
      setState({ loading: false, error: null, row, html });
    })();
    return () => { cancelled = true; };
  }, [activeId, slug, reloadKey]);
  // Session 11: opened without ?client= under the wrong client -> switch to the post's client.
  useSwitchToOwningClient('v_portal_posts', slug, !!state.notFound);

  async function onDownload() {
    if (!state.row) return;
    const { data, error } = await supabase.storage
      .from('post-packages').createSignedUrl(state.row.package_path, 300);
    if (error) return alert(error.message);
    window.location.href = data.signedUrl;
  }

  // Session 8 (B4): publish job running (parked or in flight). Read-only, no actions.
  const isPublishing = state.row?.status === 'publishing';

  // Session 6 follow-up (fix C): approved but not yet published or delivered.
  const canReopen = state.row?.status === 'ready'
    && (!state.row.publish_due_at || new Date(state.row.publish_due_at) > new Date());

  async function onReopen() {
    setBusy(true);
    try {
      const { error } = await supabase.rpc('portal_reopen_draft', { p_job_id: `seo-post-draft-${slug}` });
      if (error) throw error;
      setConfirm(false);
      setBanner({ tone: 'ok', text: 'Moved back to draft. The scheduled publish is cancelled; the post is back in review.' });
      setTimeout(() => setReloadKey((k) => k + 1), 800);
    } catch (e) {
      setConfirm(false);
      setBanner({ tone: 'err', text: `Couldn't move it back: ${e.message || e}` });
    } finally { setBusy(false); }
  }

  if (state.loading) return <p className="lede">Loading the post…</p>;
  if (state.error)   return (
    <div className="empty">
      <h2>{banner?.tone === 'ok' ? 'Back in draft' : "Couldn't open the post"}</h2>
      <p>{banner?.tone === 'ok' ? banner.text : state.error}</p>
      <p><Link to="/">← Back to your inbox</Link></p>
    </div>
  );

  return (
    <>
      <div className="reader-topbar">
        <div>
          <h1>{state.row.title}</h1>
          <p className="lede" style={{ margin: 0 }}>
            <Link to={clientPath('/blogs', activeId)}>← All blogs</Link>
            {state.row.live_url && (
              <> · <a href={state.row.live_url} target="_blank" rel="noopener noreferrer">View live</a></>
            )}
            {isAgency && state.row.status === 'ready' && (
              <> · <Link to={clientPath(`/blogs/${slug}/review`, activeId)}>Open review</Link></>
            )}
          </p>
        </div>
        <div className="review-actions" style={{ margin: 0 }}>
          {canReopen && <button className="btn" onClick={() => setConfirm(true)} disabled={busy}>Move back to draft</button>}
          {isPublishing
            ? <StatusChip status="publishing" />
            : (
              <>
                <button className="btn btn-primary" onClick={onDownload}>Download package</button>
                {/* Session 44: beside the download, on the page people actually
                    read the post on. */}
                <PostingInstructions platform={activeClient?.platform} />
              </>
            )}
        </div>
      </div>
      {banner && <div className={`banner banner-${banner.tone}`}>{banner.text}</div>}
      {isPublishing && (
        <div className="banner banner-info">
          Publishing now. The live link appears here once the post is up.
        </div>
      )}
      {state.row.status === 'ready' && (
        <div className="banner banner-info">
          Approved and scheduled{state.row.publish_due_at ? ` for ${new Date(state.row.publish_due_at).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}. This is how it will look, photos included.
        </div>
      )}
      {confirm && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => setConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Move this post back to draft?</h2>
            <p>The scheduled publish is cancelled and the post goes back to review{isAgency ? '' : '. Digital Lotus will hear about it'}. Nothing publishes until it is approved again.</p>
            <div className="review-actions">
              <button className="btn" onClick={() => setConfirm(false)} disabled={busy}>Cancel</button>
              <button className="btn btn-primary" onClick={onReopen} disabled={busy}>{busy ? 'Moving…' : 'Yes, move back to draft'}</button>
            </div>
          </div>
        </div>
      )}
      {state.row.status === 'ready' && (
        <WhyThisPost
          clientSummary={state.row.client_summary}
          primaryKeyword={state.row.primary_keyword}
          moneyPage={state.row.money_page}
          publishDate={state.row.publish_date}
          whyNow={state.row.why_now}
          selfCheck={null}
        />
      )}
      <iframe
        className="reader-frame"
        title={state.row.title}
        srcDoc={state.html}
        sandbox="allow-popups"
        referrerPolicy="no-referrer"
      />
    </>
  );
}
